import { useMutation } from '@tanstack/react-query'
import { ArrowRightIcon, MailCheckIcon } from 'lucide-react'
import { useId, useState, type FormEvent } from 'react'
import { Link } from 'react-router'

import { errorMessage } from '@/api/client'
import { register } from '@/api/endpoints'
import { AsideNotes, PublicLayout } from '@/components/PublicLayout'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAuth } from '@/auth/useAuth'

const ASIDE = [
  {
    term: 'Why only an email?',
    detail:
      'Your name and password are set on the verification page, after we know the address is real. Nothing else is collected.',
  },
  {
    term: 'What you get',
    detail:
      'A private MCP endpoint under your own slug, a bearer token, and generated configuration for Claude, ChatGPT and Gemini.',
  },
  {
    term: 'Already verified?',
    detail: 'Sign in instead — the console lives behind your identity provider account.',
  },
] as const

export function RegisterPage() {
  const emailFieldId = useId()
  const [email, setEmail] = useState('')
  const { login } = useAuth()

  const mutation = useMutation({
    mutationFn: (address: string) => register({ email: address }),
  })

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    const trimmed = email.trim()
    if (trimmed.length === 0) return
    mutation.mutate(trimmed)
  }

  if (mutation.isSuccess) {
    return (
      <PublicLayout>
        <div className="space-y-7">
          <div className="flex size-11 items-center justify-center rounded-lg border border-border bg-[var(--surface)] text-primary">
            <MailCheckIcon className="size-5" />
          </div>
          <div className="space-y-3">
            <h1 className="text-3xl font-semibold tracking-[-0.03em] text-balance text-foreground">
              Check your email.
            </h1>
            <p className="max-w-prose text-[0.9375rem] leading-relaxed text-pretty text-muted-foreground">
              If <span className="font-mono text-foreground">{mutation.variables}</span> is a
              valid address, a verification link is on its way. Open it to choose your name
              and password — that is the step that actually creates your account and issues
              your MCP endpoint.
            </p>
          </div>

          <Alert variant="info">
            <MailCheckIcon />
            <AlertTitle>The link is single-use and expires.</AlertTitle>
            <AlertDescription>
              If it has already been consumed or has aged out, come back here and register
              again with the same address.
            </AlertDescription>
          </Alert>

          <div className="flex flex-wrap items-center gap-3 pt-1">
            <Button
              variant="outline"
              onClick={() => {
                mutation.reset()
              }}
            >
              Use a different address
            </Button>
            <Button variant="ghost" asChild>
              <Link to="/">Back to the overview</Link>
            </Button>
          </div>
        </div>
      </PublicLayout>
    )
  }

  return (
    <PublicLayout aside={<AsideNotes title="Before you start" items={ASIDE} />}>
      <div className="space-y-8">
        <div className="space-y-3">
          <p className="text-[0.6875rem] font-medium tracking-[0.12em] text-primary uppercase">
            Step 1 of 2
          </p>
          <h1 className="text-3xl font-semibold tracking-[-0.03em] text-balance text-foreground">
            Create your saltseed account.
          </h1>
          <p className="max-w-prose text-[0.9375rem] leading-relaxed text-pretty text-muted-foreground">
            Give us an email address. We will send a verification link; following it is what
            creates the account, your endpoint and your bearer token.
          </p>
        </div>

        <form onSubmit={onSubmit} className="max-w-md space-y-5" noValidate>
          <div className="space-y-2">
            <Label htmlFor={emailFieldId}>Email address</Label>
            <Input
              id={emailFieldId}
              type="email"
              name="email"
              autoComplete="email"
              inputMode="email"
              required
              placeholder="you@example.com"
              value={email}
              aria-invalid={mutation.isError}
              onChange={(event) => {
                setEmail(event.target.value)
              }}
              disabled={mutation.isPending}
            />
          </div>

          {mutation.isError ? (
            <Alert variant="destructive">
              <AlertTitle>Registration could not be started</AlertTitle>
              <AlertDescription>{errorMessage(mutation.error)}</AlertDescription>
            </Alert>
          ) : null}

          <div className="flex flex-wrap items-center gap-3">
            <Button type="submit" disabled={mutation.isPending || email.trim().length === 0}>
              {mutation.isPending ? 'Sending…' : 'Send verification link'}
              {mutation.isPending ? null : <ArrowRightIcon className="size-4" />}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                login('/app')
              }}
            >
              I already have an account
            </Button>
          </div>
        </form>
      </div>
    </PublicLayout>
  )
}
